"use client";

import { useForm } from "react-hook-form";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ArrowRight, Loader2 } from "lucide-react";
import emailjs from "@emailjs/browser";
import { useRouter } from "next/navigation";

type Props = {
  title: string;
  subtitle: string;
  buttonLabel: string;
};

const contactFormSchema = z.object({
  firstName: z.string().min(1, "Enter your first name"),
  lastName: z.string().min(1, "Enter your last name"),
  email: z.email().min(1, "Enter your email"),
  message: z.string().min(1, "Enter your message"),
});

export default function ContactForm({ buttonLabel, subtitle, title }: Props) {
  const router = useRouter();
  const form = useForm<z.infer<typeof contactFormSchema>>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      message: "",
    },
  });

  const onSubmit = async (data: z.infer<typeof contactFormSchema>) => {
    const templateParams = {
      from_first_name: data.firstName,
      from_last_name: data.lastName,
      from_email: data.email,
      message: data.message,
      time: new Date().toLocaleString(),
      to_name: "Zenith Academy Team",
      message_type: "General Inquiry",
    };

    try {
      await emailjs.send(
        process.env.NEXT_PUBLIC_EMAILJS_SERVICE_ID || "your_service_id",
        process.env.NEXT_PUBLIC_EMAILJS_CONTACT_TEMPLATE_ID ||
          "your_template_id",
        templateParams,
        process.env.NEXT_PUBLIC_EMAILJS_PUBLIC_KEY || "your_public_key"
      );
      form.reset();
      router.push("/thank-you");
    } catch (error: any) {
      console.error("EmailJS error:", error);
      form.setError("firstName", {
        message: error.message,
      });
    }
  };

  return (
    <div className="bg-[#033D61] py-[73px] px-4 md:px-[30px] lg:px-[60px] xl:px-[93px]">
      <h4 className="text-[#FAFDFF] font-archivo mb-2.5 text-4xl font-semibold leading-12">
        {title}
      </h4>
      <p className="text-[#FAFDFF] font-archivo font-base mb-[53px] font-normal leading-6">
        {subtitle}
      </p>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="grid md:grid-cols-2 grid-cols-1 gap-y-4 gap-x-9"
        >
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white font-archivo text-base font-medium leading-6">
                  First Name
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="h-14 bg-[#045B91] rounded-xl px-[30px] py-4 border-none ring-offset-[#045B91] focus:ring-0 focus:outline-none focus:border-[#045B91] mt-4 w-full placeholder:text-[#9CD6FA] text-white text-sm font-medium leading-5"
                    placeholder="Enter your First Name"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white font-archivo text-base font-medium leading-6">
                  Last Name
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="h-14 bg-[#045B91] rounded-xl px-[30px] py-4 border-none ring-offset-[#045B91] focus:ring-0 focus:outline-none focus:border-[#045B91] mt-4 w-full placeholder:text-[#9CD6FA] text-white text-sm font-medium leading-5"
                    placeholder="Enter your Last Name"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="col-span-1 md:col-span-2">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white font-archivo text-base font-medium leading-6">
                    Email Address
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      className="h-14 bg-[#045B91] rounded-xl px-[30px] py-4 border-none ring-offset-[#045B91] focus:ring-0 focus:outline-none focus:border-[#045B91] mt-4 w-full placeholder:text-[#9CD6FA] text-white text-sm font-medium leading-5"
                      placeholder="Enter your Email Address"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="col-span-1 md:col-span-2">
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white font-archivo text-base font-medium leading-6">
                    Message
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={5}
                      className="bg-[#045B91] rounded-xl px-[30px] py-[25px] border-none ring-offset-[#045B91] focus:ring-0 focus:outline-none focus:border-[#045B91] mt-4 w-full placeholder:text-[#9CD6FA] text-white resize-none text-sm font-medium leading-5"
                      placeholder="Enter your Message"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="col-span-1 md:col-span-2 mt-8 flex items-center justify-center xl:justify-start">
            <Button
              type="submit"
              variant="outline"
              className="bg-transparent text-[#FAFDFF] cursor-pointer h-[54px] rounded-[40px] hover:bg-[linear-gradient(264deg,#43D9BD_-29%,#0798F2_85.45%)] transition-all duration-75 hover:[&_svg]:-rotate-45 hover:text-[#FAFDFF]"
              size="lg"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? (
                <Loader2 className="size-8! shrink-0 p-1 rounded-full text-white! transition-all duration-300 ease-in-out animate-spin" />
              ) : (
                <>
                  {buttonLabel}{" "}
                  <ArrowRight className="group-hover:hidden size-8! shrink-0 p-1 rounded-full bg-white text-primary! transition-all duration-300 ease-in-out" />
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
